import { useEffect, useMemo, useState } from 'react';
import {
  isLiability,
  outstandingPaise,
  utilisationPercent,
  type Account,
} from '../../core/accounts';
import { formatMonthKey } from '../../core/dates';
import { formatPaise } from '../../core/money';
import { periodLabel, periodMonths, type Period } from '../../core/period';
import { UNCATEGORISED, sumRollups, type Rollup } from '../../core/rollups';
import { watchAccounts } from '../../data/accounts';
import { watchCategories } from '../../data/categories';
import type { UserRecord } from '../../data/auth';
import { recomputeMonth, watchRollups } from '../../data/rollups';
import type { Category } from '../../core/categories';
import type { MonthKey } from '../../core/dates';

interface Props {
  record: UserRecord;
  period: Period;
}

/**
 * The dashboard (FR-DASH-01 to FR-DASH-09).
 *
 * **Reads rollup documents and small collections only — never a transactions query.**
 * A financial year is twelve rollup reads plus ~32 accounts, and that number does not
 * change as the book grows from 637 records to 60,000 (FR-NFR-03). Anything here that
 * needed a transaction scan would defeat the whole design, so there is deliberately no
 * such call in this file.
 */
export function DashboardScreen({ record, period }: Props) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [rollups, setRollups] = useState<Map<MonthKey, Rollup> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const months = useMemo(() => periodMonths(period), [period]);

  useEffect(() => watchAccounts(setAccounts), []);
  // Rollup buckets are keyed by category id (SD-04). Resolving them to names is the
  // only reason this screen reads the category list at all — it still reads no
  // transactions (FR-DASH-03).
  useEffect(() => watchCategories(setCategories), []);

  /** `uncategorised` is a synthetic bucket, not a category document, so it is named here. */
  const categoryName = (id: string) =>
    id === UNCATEGORISED
      ? 'Uncategorised'
      : (categories.find((c) => c.id === id)?.name ?? id);
  useEffect(() => {
    setRollups(null);
    return watchRollups(months, setRollups);
  }, [months.join()]);

  const accountType = useMemo(() => {
    const map = new Map(accounts.map((a) => [a.id, a.type]));
    return (id: string) => map.get(id);
  }, [accounts]);

  const total = useMemo(
    () => sumRollups(rollups ? [...rollups.values()] : []),
    [rollups],
  );

  const live = accounts.filter((a) => !a.archived);
  const cards = live.filter((a) => a.type === 'credit_card');
  const loans = live.filter((a) => a.type === 'loan');
  const liquid = live.filter((a) => !isLiability(a.type));

  async function recompute() {
    setNotice(null);
    for (const monthKey of months) {
      setBusy(monthKey);
      await recomputeMonth(monthKey, accountType);
    }
    setBusy(null);
    setNotice(`Rebuilt ${months.length} months from their transactions.`);
  }

  if (rollups === null) return <p className="muted">Loading…</p>;

  return (
    <>
      <div className="head-row">
        <h1>{periodLabel(period)}</h1>
        <button type="button" className="btn-link" disabled={busy !== null} onClick={() => void recompute()}>
          {busy ? `Rebuilding ${formatMonthKey(busy)}…` : 'Rebuild totals'}
        </button>
      </div>

      {notice && <p className="muted" aria-live="polite">{notice}</p>}

      {/* The three figures, sized equally, with the third visibly the sum of the first
          two — that relationship is the point of the definition, and the layout should
          make it self-evident rather than something to work out. */}
      <div className="figures">
        <Figure label="Expenses" paise={total.expensePaise} />
        <span className="figures-op" aria-hidden="true">+</span>
        <Figure label="Savings & investments" paise={total.savingsPaise} />
        <span className="figures-op" aria-hidden="true">=</span>
        <Figure label="Total outflow" paise={total.totalOutflowPaise} emphasis />
      </div>

      <p className="muted">
        Income <strong>{formatPaise(total.incomePaise)}</strong> · net{' '}
        <strong>{formatPaise(total.incomePaise - total.totalOutflowPaise, { signed: true })}</strong>
        {total.transferPaise > 0 && (
          <> · {formatPaise(total.transferPaise)} moved between own accounts, in none of the three</>
        )}
        {total.lendingPaise > 0 && (
          <> · {formatPaise(total.lendingPaise)} lent out, tracked in receivables</>
        )}
        {' · '}
        {total.txnCount} transactions
      </p>

      <div className="panel-grid">
        <section className="panel">
          <h2>Accounts</h2>
          {liquid.length === 0 ? (
            <p className="muted tiny">No accounts yet.</p>
          ) : (
            <table className="table compact">
              <tbody>
                {[...liquid]
                  .sort((a, b) => b.balancePaise - a.balancePaise)
                  .slice(0, 6)
                  .map((a) => (
                    <tr key={a.id}>
                      <td>{a.name}</td>
                      <td className="num">{formatPaise(a.balancePaise)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
          {liquid.length > 6 && (
            <p className="muted tiny">
              and {liquid.length - 6} more ·{' '}
              {formatPaise(liquid.reduce((s, a) => s + a.balancePaise, 0))} in total
            </p>
          )}
        </section>

        <section className="panel">
          <h2>Cards</h2>
          {cards.length === 0 ? (
            <p className="muted tiny">No cards yet.</p>
          ) : (
            <table className="table compact">
              <tbody>
                {cards.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td className="num">
                      {formatPaise(outstandingPaise(c.balancePaise))}
                      <span className="muted tiny">
                        {' '}
                        {utilisationPercent(c.balancePaise, c.creditLimitPaise ?? 0)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {cards.length > 0 && (
            <p className="muted tiny">
              {formatPaise(cards.reduce((s, c) => s + outstandingPaise(c.balancePaise), 0))}{' '}
              outstanding in total
            </p>
          )}
        </section>

        <section className="panel">
          <h2>Where it went</h2>
          {Object.keys(total.byCategory).length === 0 ? (
            <p className="muted tiny">No categorised spending in this period.</p>
          ) : (
            <table className="table compact">
              <tbody>
                {Object.entries(total.byCategory)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 6)
                  .map(([categoryId, paise]) => (
                    <tr key={categoryId}>
                      <td>{categoryName(categoryId)}</td>
                      <td className="num">{formatPaise(paise)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </section>

        {loans.length > 0 && (
          <section className="panel">
            <h2>Loan</h2>
            <table className="table compact">
              <tbody>
                {loans.map((l) => (
                  <tr key={l.id}>
                    <td>{l.name}</td>
                    <td className="num">{formatPaise(outstandingPaise(l.balancePaise))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>

      <p className="muted tiny">
        Signed in as {record.displayName ?? record.email} · read from {months.length} monthly
        totals, not from the transactions themselves — so this stays the same speed as the
        book grows.
      </p>
    </>
  );
}

function Figure({ label, paise, emphasis }: { label: string; paise: number; emphasis?: boolean }) {
  return (
    <div className={emphasis ? 'figure emphasis' : 'figure'}>
      <div className="figure-label">{label}</div>
      <div className="figure-value">{formatPaise(paise, { compact: true })}</div>
    </div>
  );
}
